import React, { useState, useRef } from 'react';
import api, { assetUrl } from '../utils/api';
import toast from 'react-hot-toast';
import { Settings, Trash2, X, Save, Upload, Image as ImageIcon } from 'lucide-react';

const MAX_LOGO_BYTES = 2 * 1024 * 1024;

export default function ProjectSettingsModal({ project, onClose, onUpdated, onDeleted }) {
  const [form, setForm] = useState({ name: project.name, description: project.description || '', base_url: project.base_url || '' });
  const [logoUrl, setLogoUrl] = useState(project.logo_url || '');
  const [logoBusy, setLogoBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const fileRef = useRef(null);

  // The logo saves on its own (multipart) rather than with the JSON form, so
  // the preview and the docs pages update as soon as a file is picked.
  async function uploadLogo(file) {
    if (!file) return;
    if (file.size > MAX_LOGO_BYTES) return toast.error('Logo must be under 2MB');
    const payload = new FormData();
    payload.append('logo', file);
    setLogoBusy(true);
    try {
      const res = await api.post(`/projects/${project.id}/logo`, payload);
      setLogoUrl(res.data.logo_url || '');
      onUpdated && onUpdated(res.data);
      toast.success('Logo updated');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload logo');
    } finally {
      setLogoBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function removeLogo() {
    setLogoBusy(true);
    try {
      const res = await api.delete(`/projects/${project.id}/logo`);
      setLogoUrl('');
      onUpdated && onUpdated(res.data);
      toast.success('Logo removed');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to remove logo');
    } finally { setLogoBusy(false); }
  }

  async function save(e) {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await api.put(`/projects/${project.id}`, form);
      toast.success('Project updated!');
      onUpdated && onUpdated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update');
    } finally { setSaving(false); }
  }

  async function deleteProject() {
    try {
      await api.delete(`/projects/${project.id}`);
      toast.success('Project deleted');
      onDeleted && onDeleted();
      onClose();
    } catch { toast.error('Failed to delete'); }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
        <div style={sty.header}>
          <div style={sty.headerLeft}>
            <Settings size={18} style={{ color: 'var(--accent)' }} />
            <h3 className="modal-title" style={{ margin: 0 }}>Project Settings</h3>
          </div>
          <button style={sty.closeBtn} onClick={onClose}><X size={16} /></button>
        </div>

        <form onSubmit={save}>
          <div className="form-group">
            <label className="form-label">Project Name *</label>
            <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} required />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea rows={3} style={{ resize: 'vertical' }} value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What does this project do?" />
          </div>
          <div className="form-group">
            <label className="form-label">Base URL</label>
            <input value={form.base_url} onChange={e => setForm(f => ({ ...f, base_url: e.target.value }))}
              placeholder="https://api.example.com" className="mono" />
            <div style={sty.hint}>Used as the base for all API requests. Supports <code style={sty.code}>{'{{ENV_VAR}}'}</code> substitution.</div>
          </div>

          <div className="form-group">
            <label className="form-label">Project Logo</label>
            <div style={sty.logoRow}>
              <div style={sty.logoPreview}>
                {logoUrl
                  ? <img src={assetUrl(logoUrl)} alt="Project logo" style={sty.logoImg} />
                  : <ImageIcon size={20} style={{ color: 'var(--text-muted)' }} />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-ghost btn-sm" disabled={logoBusy}
                    onClick={() => fileRef.current?.click()}>
                    <Upload size={13} /> {logoBusy ? 'Uploading…' : logoUrl ? 'Replace' : 'Upload Logo'}
                  </button>
                  {logoUrl && (
                    <button type="button" className="btn btn-ghost btn-sm" disabled={logoBusy} onClick={removeLogo}>
                      <Trash2 size={13} /> Remove
                    </button>
                  )}
                </div>
                <div style={sty.hint}>PNG, JPEG, WebP or GIF — up to 2MB. Shown on this project's documentation pages.</div>
              </div>
            </div>
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif"
              style={{ display: 'none' }} onChange={e => uploadLogo(e.target.files[0])} />
          </div>

          <div style={sty.divider} />

          {/* Danger zone */}
          <div style={sty.dangerZone}>
            <div style={sty.dangerTitle}>Danger Zone</div>
            {!confirmDelete ? (
              <div style={sty.dangerRow}>
                <div>
                  <div style={sty.dangerLabel}>Delete Project</div>
                  <div style={sty.dangerDesc}>Permanently delete this project and all its endpoints, environments, and schemas.</div>
                </div>
                <button type="button" className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
                  <Trash2 size={13} /> Delete
                </button>
              </div>
            ) : (
              <div style={sty.confirmDelete}>
                <p style={{ color: 'var(--accent-red)', fontSize: 13, marginBottom: 12 }}>
                  ⚠️ This will permanently delete <strong>{project.name}</strong> and all associated data. This cannot be undone.
                </p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(false)}>Cancel</button>
                  <button type="button" className="btn btn-danger btn-sm" onClick={deleteProject}>Yes, Delete Project</button>
                </div>
              </div>
            )}
          </div>

          <div className="modal-actions">
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              <Save size={13} /> {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const sty = {
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 10 },
  closeBtn: { background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4, display: 'flex', borderRadius: 5 },
  hint: { fontSize: 11, color: 'var(--text-muted)', marginTop: 5 },
  logoRow: { display: 'flex', alignItems: 'center', gap: 12 },
  logoPreview: {
    width: 56, height: 56, borderRadius: 10, flexShrink: 0, display: 'flex', alignItems: 'center',
    justifyContent: 'center', background: '#fff', border: '1px solid var(--border)', overflow: 'hidden',
  },
  logoImg: { maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' },
  code: { fontFamily: 'var(--mono)', color: 'var(--accent)', fontSize: 11 },
  divider: { borderTop: '1px solid var(--border)', margin: '20px 0' },
  dangerZone: { background: 'rgba(240,92,92,0.05)', border: '1px solid rgba(240,92,92,0.2)', borderRadius: 8, padding: 16, marginBottom: 4 },
  dangerTitle: { fontSize: 11, fontWeight: 700, color: 'var(--accent-red)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 12 },
  dangerRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 },
  dangerLabel: { fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 3 },
  dangerDesc: { fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.5 },
  confirmDelete: {},
};
